<template>
  <v-container
    fluid
    class="login-container"
  >
    <v-layout
      column
      align-center
      :style="{'margin-top': $vuetify.breakpoint.smAndUp ? '100px' : null}"
    >
      <!-- Logo -->
      <a href="https://www.myshopradio.de">
        <img
          src="/static/logo_myshopradio_tv_black.svg"
          height="56px"
          class="clickable"
          :class="{
            'login-logo-desktop': $vuetify.breakpoint.smAndUp,
            'login-logo-mobile': $vuetify.breakpoint.xs,
          }"
        >
      </a>
      <!-- Title -->
      <h1
        v-if="$vuetify.breakpoint.smAndUp"
        class="display-3 grey--text text--darken-4 my-3"
        style="font-weight: lighter"
      >
        {{ $t('signupToLogin') }}
      </h1>
      <!-- Signup Link -->
      <v-layout
        v-show="!codeAuthentication"
        justify-center
      >
        <span
          class="mb-3"
          style="font-size: 18px"
        >
          <span
            class="grey--text text--darken-3"
          >{{ $t('signupPreQuestion') }}&nbsp;</span>
          <router-link
            to="/signup"
            style="text-decoration: none"
          >{{
            $t('loginToSignup')
          }}</router-link>
        </span>
      </v-layout>
      <v-card
        v-show="!codeAuthentication"
        flat
        color="transparent"
        class="login-card"
        width="100%"
        :style="{
          'max-width': $vuetify.breakpoint.smAndUp ? '480px' : 'initial',
        }"
      >
        <!-- Fields -->
        <v-card-actions>
          <v-layout
            column
            justify-center
          >
            <div
              v-if="magiclinkExpired"
              class="subheading warning--text mb-3"
            >
              {{ $t('magiclinkExpiredPleaseLoginManually') }}
            </div>
            <v-text-field
              ref="usernameOrEmailInput"
              v-model="usernameOrEmail"
              required
              type="email"
              validate-on-blur
              name="usernameOrEmail"
              class="min-input-width-fix"
              :rules="[validateUsernameOrEmail]"
              :label="$t('email')"
              @keyup.native.enter="login(usernameOrEmail, password)"
            />
            <v-layout column>
              <v-text-field
                ref="passwordInput"
                v-model="password"
                required
                name="password"
                class="min-input-width-fix"
                :class="{
                  'prevent-login-box-growth': $vuetify.breakpoint.width > 400,
                }"
                :label="$t('password')"
                :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                :type="showPassword ? 'text' : 'password'"
                :error-messages="$store.state.common.loginErrors"
                @click:append="togglePasswordVisibility()"
                @keyup.native.enter="login(usernameOrEmail, password)"
              />
              <div
                class="
                  pseudo-anchor
                  grey--text
                  text--darken-1
                  msr--text--hover
                  ml-auto
                "
                data-cy="password-forgotten-link"
                style="text-decoration: none"
                @click="goToPasswordForgottenPage()"
              >
                {{ $t('forgotPassword') }}
              </div>
            </v-layout>
            <!-- Buttons -->
            <v-layout
              justify-start
              align-baseline
              class="mt-3"
            >
              <v-hover>
                <v-btn
                  slot-scope="{hover}"
                  data-cy="login-btn"
                  large
                  round
                  style="min-width: 168px"
                  class="min-btn-height-fix mx-0"
                  :block="$vuetify.breakpoint.xs"
                  :dark="hover"
                  :flat="hover"
                  :outline="!hover"
                  :class="{'msr--text': !hover, msr: hover}"
                  :loading="busy"
                  @click="login(usernameOrEmail, password)"
                >
                  {{ $t('signupToLogin') }}
                </v-btn>
              </v-hover>
            </v-layout>
          </v-layout>
        </v-card-actions>
      </v-card>
      <v-layout
        v-if="codeAuthentication"
        justify-center
      >
        <span
          class="mb-3"
          style="font-size: 18px"
        >
          <span
            v-if="codeAuthentication.method == 'sms'"
            class="grey--text text--darken-3"
          >
            {{ $t('smsAuthInfoBeforeNumber') }} ***********-{{
              codeAuthentication.mobile_number_suffix
            }}
            {{ $t('smsAuthInfoAfterNumber') }}
          </span>
          <span
            v-if="codeAuthentication.method == 'otp'"
            class="grey--text text--darken-3"
          >
            {{ $t('otpAuthInfo') }}
          </span>
        </span>
      </v-layout>
      <v-card
        v-if="codeAuthentication"
        flat
        color="transparent"
        class="login-card"
        width="100%"
        :style="{
          'max-width': $vuetify.breakpoint.smAndUp ? '480px' : 'initial',
        }"
      >
        <v-container>
          <v-layout
            column
            align-center
          >
            <CodeInput
              ref="codeinput"
              :field-width="codeInputFieldWidth"
              @complete="codeEnterComplete"
            />
          </v-layout>
          <v-layout
            v-if="show2FAAdminResetHint"
            column
            align-center
            class="mt-3 grey--text text-xs-center"
          >
            {{ $t('twoFactorAuthenticationResetHint') }}
          </v-layout>
          <v-layout
            column
            align-center
            class="mt-3"
          >
            <v-alert
              outline
              :value="$store.state.common.loginErrors[0]"
              icon="warning"
            >
              {{ $store.state.common.loginErrors[0] }}
            </v-alert>
          </v-layout>
        </v-container>
      </v-card>
      <v-layout
        v-if="showDocmgrSignupText"
        align-center
        class="mt-5 text-xs-center docmgr-notice"
        style="max-width: 720px"
      >
        {{ $t('signupDocmgrNotice') }}
      </v-layout>
      <!-- Confirmation to data privacy etc. info -->
      <v-layout
        align-center
        class="mt-5 grey--text text-xs-center"
        style="max-width: 720px"
      >
        <small>* {{ $t('signupLoginConfirmationByClickInfo') }}</small>
      </v-layout>
    </v-layout>
  </v-container>
</template>

<script>
import CodeInput from 'vue-verification-code-input';

import {redirectToExternalUrl, urlQueryParamToBoolean} from '@/utils';

import runtime from '@/mixins/runtime.js';
import codeInputWorkarounds from '@/mixins/codeInputWorkarounds';

export default {
  components: {
    CodeInput,
  },
  mixins: [runtime, codeInputWorkarounds],
  data() {
    return {
      busy: false,
      showPassword: false,
      usernameOrEmail: '',
      password: '',
      codeAuthentication: null,
      showDocmgrSignupNotice: false,
      show2FAAdminResetHint: false,
    };
  },
  computed: {
    isProbablyEmail() {
      return this.usernameOrEmail.indexOf('@') !== -1;
    },
    useVerticalSnackbar() {
      return this.$vuetify.breakpoint.width < 900;
    },
    useLessPaddingOnSnackbar() {
      return this.useVerticalSnackbar;
    },
    useSmallerFontForSnackbar() {
      return this.$vuetify.breakpoint.xsOnly;
    },
    showDocmgrSignupText() {
      return this.$route.query.docmgr;
    },
    magiclinkExpired() {
      return (
        this.$route.query &&
        urlQueryParamToBoolean(this.$route.query.magiclink_expired)
      );
    },
  },
  mounted() {
    this.$store.dispatch('RETRIEVE_IS_USING_WEBVIEW');

    // Note: 'email' route query is handled within beforeRouteEnter().
    if (this.$refs.usernameOrEmailInput) {
      this.$refs.usernameOrEmailInput.focus();
    }
  },
  methods: {
    goToPasswordForgottenPage() {
      if (!!this.usernameOrEmail || !!this.password) {
        const confirmed = confirm(
          this.$t('confirmPageNavigationDataWillBeLost')
        );
        if (!confirmed) {
          return;
        }
      }
      this.$router.push({name: 'passwordforgotten'});
    },
    reloadPage() {
      window.location.reload();
    },
    validateUsernameOrEmail(usernameOrEmail) {
      if (usernameOrEmail.trim() === '') {
        return '';
      }

      if (this.isProbablyEmail) {
        // See: https://stackoverflow.com/questions/46155
        var regexUnicodeEmail =
          /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
        if (!regexUnicodeEmail.test(usernameOrEmail)) {
          return this.$t('invalidEmail');
        }
      } else {
        const regexUsername = /^([0-9a-zA-z_ \-\.äöüß])+$/gm;
        if (
          !regexUsername.test(usernameOrEmail) ||
          usernameOrEmail.indexOf('\t') !== -1
        ) {
          return this.$t('invalidUsername');
        }
      }

      return true;
    },
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    login(usernameOrEmail, password, code = null) {
      var vm = this;

      this.detectRuntimeEnvironment();

      // Validate.
      if (!usernameOrEmail.trim()) {
        this.$store.commit('SET_LOGIN_ERRORS', [this.$t('usernameRequired')]);
        return;
      }
      if (!password.trim()) {
        this.$store.commit('SET_LOGIN_ERRORS', [this.$t('passwordRequired')]);
        return;
      }
      this.$store.commit('SET_LOGIN_ERRORS', []);

      this.busy = true;
      this.$api.login(usernameOrEmail, password, code).then(
        (response) => {
          if (response.data.coderequired) {
            this.codeAuthentication = response.data.coderequired;
            this.show2FAAdminResetHint =
              response.data.coderequired.is_being_managed;
            this.$nextTick(() => {
              this.focusCodeInput();
              this.setupPasteCatcher();
            });
            return;
          }

          vm.$store.commit('SET_CURRENT_USER', response.data.user);
          vm.$bus.$emit('user-available');

          // Show drawer initially on desktop, even after repeated
          // logouts/logins.
          if (vm.$vuetify.breakpoint.lgAndUp) {
            vm.$store.commit('SET_SHOW_DRAWER', true);
          }

          vm.resetInputs();

          this.handleOptionalExternalRedirectQuery();

          let next = 'dashboard';
          if (this.$route.query.redirect) {
            next = this.$route.query.redirect;
          }
          vm.$router.push(next);

          vm.$store.dispatch('LIST_CAMPAIGNS').then(() => {
            vm.busy = false;
          });
        },
        (error) => {
          vm.busy = false;

          // Clear password on failed login just to be sure.
          // keep password if in code authentication stage
          if (this.codeAuthentication) {
            // yeah, codeinput component should definitely have a clear method ..
            this.clearCodeInput();
            this.focusCodeInput();
            vm.$store.commit('SET_LOGIN_ERRORS', [vm.$t('codeLoginFailed')]);
          } else {
            vm.$store.commit('SET_LOGIN_ERRORS', [vm.$t('loginFailed')]);
          }
        }
      );
    },
    codeEnterComplete(code) {
      this.login(this.usernameOrEmail, this.password, code);
    },
    resetInputs() {
      this.usernameOrEmail = '';
      this.password = '';
    },
    /**
     * If an email was passed as route query param, fill into form.
     *
     * Also focus the password input afterwards. This is meant to
     * speed up logging back in after a password change.
     */
    setInitialEmail() {
      const initialEmail = this.$route.query.email;
      if (initialEmail) {
        this.usernameOrEmail = initialEmail;
        this.$refs.passwordInput.focus();
      }
    },
    handleOptionalExternalRedirectQuery() {
      if (this.$route.query.externalredirect) {
        return redirectToExternalUrl(this, this.$route.query.externalredirect);
      }
    },
  },
  beforeRouteEnter(to, from, next) {
    // https://router.vuejs.org/en/advanced/navigation-guards.html
    next((vm) => {
      vm.setInitialEmail();
    });
  },
};
</script>

<style>
.less-padding-snackbar.v-snack.v-snack--active
  .v-snack__wrapper
  .v-snack__content {
  padding: 14px 14px 14px;
}
</style>

<style>
.prevent-login-box-growth.input-group
  .input-group__details
  .input-group__messages
  .input-group__error {
  width: 0;
  white-space: nowrap;
}
</style>

<style scoped>
@media (max-width: 599px) {
  .login-container {
    padding: 0;
  }
}

.login-logo-desktop {
  position: fixed;
  top: 0;
  left: 0;
  margin: 60px 0 0 60px;
}
.login-logo-mobile {
  position: relative;
  top: -24px;
}

.login-card {
  z-index: 4; /* Should be above the toolbar. */
}

.min-input-width-fix {
  min-width: 200px;
}

.min-btn-height-fix {
  min-height: 36px;
}

.docmgr-notice {
  font-size: larger;
  border: 1px solid var(--msr-color-primary);
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  border-radius: 4px;
}
</style>



// WEBPACK FOOTER //
// src/pages/Login.vue